<script setup lang="ts">
import { Person } from '@/quote/domain/Person';
import { MEMBER_TYPES } from '@hfc/common/src/types/MemberTypes';
import DateSelector from '@/shared/infrastructure/components/DateSelector.vue';
import { GENDERS } from '@hfc/common/src/types/Genders';
import { Field } from 'vee-validate';
import { computed } from 'vue';
import QuestionButtonSet from '@/application/shared/infrastructure/components/questionTypes/QuestionButtonSet.vue';
import { dateHelpers } from '@hfc/ui-core';

const { getDateFromAge } = dateHelpers();

const props = withDefaults(defineProps<{
  person: Partial<Person>,
  index: number,
  showIndex: boolean,
  showRequireCoverage?: boolean,
  dateOfBirthLabel: string,
  minAge?: number,
  maxAge?: number,
  customLayout?: string,
  showSeparator?: boolean,
  genderClass?: string,
  dateOfBirthClass?: string
  showRequiredOnLabel?: boolean,
}>(), {
  showRequireCoverage: true,
  showSeparator: true,
  customLayout: 'flex flex-col sm:flex-row',
  minAge: 0,
  maxAge: 120,
  dateOfBirthClass: 'h-16 w-64'
});

const canRemove = props.person.type !== MEMBER_TYPES.APPLICANT;

const localGender = computed(() => props.person.gender);
const localDateOfBirth = computed(() => props.person.date_of_birth);
const localApplying = computed(() => props.person.applying);

interface ButtonSetOptions {
  text: string,
  value: string
}

const getGenders = (): Array<ButtonSetOptions> => {
  return Object.values(GENDERS).filter(gender => gender !== GENDERS.OTHER).map(gender => ({
    value: gender,
    text: gender.charAt(0).toUpperCase() + gender.slice(1)
  }));
};
</script>
<template>
  <div class="relative py-5" :class="{'border-t-2': showSeparator}">
    <div class="flex flex-row w-full justify-items-center">
      <div class="flex flex-1">
        <h2 class="text-lg font-semibold capitalize">
          {{ person.type === MEMBER_TYPES.APPLICANT ? 'Primary Applicant' : person.type }} <span v-if="showIndex">{{ index + 1 }}</span>
          <slot name="after-header"></slot>
        </h2>
      </div>
      <div class="flex flex-1 justify-end">
        <button v-if="canRemove" tabindex="-1" type="button" @click.prevent="$emit('remove', {index, type: person.type})" class="text-red-300 text-lg font-bold transform duration-300 hover:text-red-500">
          <hfc-icon icon="times" class="px-4" />
        </button>
      </div>
    </div>
    <div class="mt-4 space-y-4 sm:space-y-0" :class="customLayout">
      <div class="flex flex-1 sm:flex-none flex-col mr-0 sm:mr-4">
        <label class="font-medium mb-1 text-sky-700 block">
          Gender <span v-if="showRequiredOnLabel" class="font-serif">*</span>
        </label>
        <Field v-model="localGender" v-slot="{ field, errorMessage }" :name="`person-${person.type}-${index}-gender`" rules="required">
          <QuestionButtonSet class="w-full" :options="getGenders()" :id="`person-${person.type}-${index}-gender`" v-bind:model-value="field.value" @update:modelValue="val => $emit('updatePerson', {... person, gender: val })" :auto-focus="index === 0" :size="genderClass"></QuestionButtonSet>
          <span class="text-red">{{errorMessage}}</span>
        </Field>
      </div>
      <div class="flex flex-1 md:flex-none flex-col mr-0 sm:mr-4" :class="dateOfBirthClass">
        <Field v-model="localDateOfBirth" v-slot="{ field, errorMessage, meta }" :name="`person-${person.type}-${index}-date`" :rules="{ required: true, not_one_of: [0], min_date: getDateFromAge(minAge), max_date: getDateFromAge(maxAge) }">
          <DateSelector :label="dateOfBirthLabel" :id="`person-${person.type}-${index}-dob`" v-bind:model-value="field.value" @update:modelValue="val => $emit('updatePerson', {... person, date_of_birth: val })" :has-errors="!meta.valid && meta.validated" :size="dateOfBirthClass" :show-required-on-label="showRequiredOnLabel"/>
          <span v-if="errorMessage" class="error">{{errorMessage}}</span>
        </Field>
      </div>
      <slot name="after-fields"></slot>
    </div>
    <div v-if="showRequireCoverage" class="flex flex-row items-center mt-2">
      <label for="require-coverage" class="block font-medium mr-4">Require Coverage:</label>
      <input id="require-coverage" type="checkbox" class="border-gray-300 rounded-md w-4 h-4"
        v-model="localApplying" @change="$emit('updatePerson', {... person, applying: !localApplying })" />
    </div>
  </div>
</template>
